import { useState } from "react";
import {
  Drawer,
  Button,
  Table,
  message,
  Spin,
  Alert,
  Space,
  Tag,
  Popconfirm,
  Form,
  Input,
  Select,
  Switch,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_WIDE_WIDTH,
} from "../../../../components";
import {
  useStoresQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useDeleteStoreMutation,
} from "../../../../Services";

const { Text } = Typography;

interface Store {
  id: string;
  name: string;
  main_store: number;
  type: string;
  latitude: string | null;
  longitude: string | null;
  status: string;
  created_at: string;
}

interface Props {
  open: boolean;
  tenantId?: string;
  onClose: () => void;
}

export const StoreManageDrawer: React.FC<Props> = ({
  open,
  tenantId,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [form] = Form.useForm();
  const [storeType, setStoreType] = useState<string>("shop");

  const {
    data: stores = [],
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  } = useStoresQuery(
    { page: 1, limit: 100, tenant_id: tenantId },
    { enabled: open },
  );

  const createMutation = useCreateStoreMutation();
  const updateMutation = useUpdateStoreMutation();
  const deleteMutation = useDeleteStoreMutation();

  const mutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const loading = isFetching || mutationPending;

  const handleCreate = () => {
    setEditingStore(null);
    setIsEditing(false);
    setIsCreating(true);
    setStoreType("shop");
    form.resetFields();
    form.setFieldsValue({ type: "shop", main_store: false });
  };

  const handleEdit = (store: Store) => {
    setIsCreating(false);
    setIsEditing(true);
    setEditingStore(store);
    setStoreType(store.type);
    form.setFieldsValue({
      name: store.name,
      type: store.type,
      main_store: !!store.main_store,
      latitude: store.latitude,
      longitude: store.longitude,
    });
  };

  const handleCancelForm = () => {
    setIsCreating(false);
    setIsEditing(false);
    setEditingStore(null);
    form.resetFields();
  };

  const handleSave = async (values: any) => {
    try {
      if (isCreating) {
        const res = await createMutation.mutateAsync({
          name: values.name,
          type: values.type,
          main_store: values.main_store ? 1 : 0,
          latitude: values.latitude || null,
          longitude: values.longitude || null,
          ...(tenantId ? { tenant_id: tenantId } : {}),
        });
        if (res?.success) {
          message.success("Store created successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to create store.");
        }
      } else if (isEditing && editingStore) {
        const res = await updateMutation.mutateAsync({
          id: editingStore.id,
          name: values.name,
          type: values.type,
          main_store: values.main_store ? 1 : 0,
          latitude: values.latitude || null,
          longitude: values.longitude || null,
          ...(tenantId ? { tenant_id: tenantId } : {}),
        });
        if (res?.success) {
          message.success("Store updated successfully.");
          handleCancelForm();
        } else {
          throw new Error(res?.message || "Failed to update store.");
        }
      }
    } catch (err: any) {
      message.error(err.message || "Error saving store.");
    }
  };

  const handleDelete = async (store: Store) => {
    try {
      const res = await deleteMutation.mutateAsync({
        id: Number(store.id),
        tenant_id: tenantId,
      });
      if (res?.success) {
        message.success("Store deleted successfully.");
      } else {
        throw new Error(res?.message || "Failed to delete store.");
      }
    } catch (err: any) {
      message.error(err.message || "Error deleting store.");
    }
  };

  const columns = [
    {
      title: "Store Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, store: Store) => (
        <Space>
          <Text className="font-semibold text-xs">{text}</Text>
          {!!store.main_store && (
            <Tag color="blue" className="text-[10px] leading-none">
              MAIN
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 90,
      render: (type: string) => (
        <Tag
          color={type === "vehicle" ? "orange" : "default"}
          className="text-[10px]"
        >
          {type}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 80,
      render: (status: string) => (
        <Tag
          color={status === "Active" ? "green" : "red"}
          className="text-[10px]"
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, store: Store) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined className="text-blue-500" />}
            size="small"
            onClick={() => handleEdit(store)}
          />
          <Popconfirm
            title="Are you sure you want to delete this store?"
            onConfirm={() => handleDelete(store)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={
                deleteMutation.isPending &&
                deleteMutation.variables?.id === Number(store.id)
              }
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const errorMsg = isError
    ? (error as any)?.message || "Failed to load stores."
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={DRAWER_WIDE_WIDTH}
      title={drawerTitle("Manage Stores", "Create, edit, and manage stores")}
      destroyOnClose
      footer={drawerFooter({ actions: [{ label: "Close", onClick: onClose }] })}
    >
      <div className="flex flex-col gap-4 h-full">
        {errorMsg && (
          <Alert
            message="Connection Error"
            description={
              <div className="flex flex-col gap-2">
                <p className="text-xs text-red-800">{errorMsg}</p>
                <Button
                  size="small"
                  type="primary"
                  danger
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  className="w-fit text-xs"
                >
                  Retry Connection
                </Button>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {!errorMsg && !isLoading && (
          <Alert
            message={
              <div className="flex items-center justify-between">
                <Space>
                  <CheckCircleOutlined className="text-green-500" />
                  <span className="text-xs font-semibold text-green-800">
                    Connected &mdash; {stores.length} store(s)
                  </span>
                </Space>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isFetching && !isCreating && !isEditing}
                  className="text-[11px] h-6"
                >
                  Refresh
                </Button>
              </div>
            }
            type="success"
            showIcon={false}
            className="!border-green-100 !bg-green-50"
          />
        )}

        {!isCreating && !isEditing && (
          <div className="flex justify-between items-center bg-gray-50 p-2.5 rounded-lg border">
            <Text className="text-xs text-gray-500">
              Manage stores in the POS system.
            </Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="small"
              className="!bg-black !border-black hover:!bg-gray-800 text-xs"
            >
              Add Store
            </Button>
          </div>
        )}

        {(isCreating || isEditing) && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <ShopOutlined className="text-slate-500" />
                {isCreating ? "New Store" : "Edit Store"}
              </span>
              <Button
                type="text"
                size="small"
                shape="circle"
                icon={<CloseOutlined className="text-xs" />}
                onClick={handleCancelForm}
              />
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              size="middle"
            >
              <Form.Item
                name="name"
                label="Store Name"
                rules={[{ required: true, message: "Store name is required." }]}
              >
                <Input placeholder="e.g. Main Store" className="text-xs" />
              </Form.Item>

              <Form.Item
                name="type"
                label="Store Type"
                rules={[{ required: true, message: "Store type is required." }]}
              >
                <Select
                  onChange={(val) => setStoreType(val)}
                  options={[
                    { value: "shop", label: "Shop" },
                    { value: "vehicle", label: "Vehicle" },
                  ]}
                />
              </Form.Item>

              {storeType === "vehicle" && (
                <Space className="w-full" size="small">
                  <Form.Item
                    name="latitude"
                    label="Latitude"
                    className="flex-1"
                  >
                    <Input placeholder="e.g. 6.9271" className="text-xs" />
                  </Form.Item>
                  <Form.Item
                    name="longitude"
                    label="Longitude"
                    className="flex-1"
                  >
                    <Input placeholder="e.g. 79.8612" className="text-xs" />
                  </Form.Item>
                </Space>
              )}

              <Form.Item
                name="main_store"
                label="Main Store"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item className="mb-0">
                <Space className="w-full justify-end">
                  <Button size="small" onClick={handleCancelForm}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={
                      createMutation.isPending || updateMutation.isPending
                    }
                    size="small"
                    className="!bg-black !border-black hover:!bg-gray-800"
                  >
                    Save Changes
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}

        <div className="flex-grow overflow-auto relative min-h-[200px]">
          {isLoading && (
            <div className="flex items-center justify-center h-40">
              <Spin tip="Loading stores..." size="large" />
            </div>
          )}

          {stores.length > 0 && (
            <Table
              dataSource={stores}
              columns={columns}
              rowKey="id"
              size="small"
              pagination={false}
              className="border rounded-lg"
              locale={{ emptyText: "No stores found." }}
            />
          )}

          {stores.length === 0 && !isLoading && !isCreating && !isEditing && (
            <div className="text-center text-gray-400 text-sm py-10">
              No stores found. Click "Add Store" to create one.
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
